import { registerSW } from 'virtual:pwa-register';
import { setUpdateAvailable } from './appStatus';

const UPDATE_CHECK_MS = 30 * 60_000;
let update: ((reload?: boolean) => Promise<void>) | null = null;

/**
 * Registers the service worker. A new version is not activated on its own: the header
 * shows "Dostępna nowa wersja" and the user decides when to reload.
 */
export function startPwa(): void {
  if (!('serviceWorker' in navigator)) return;
  update = registerSW({
    immediate: true,
    onNeedRefresh: () => setUpdateAvailable(true),
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // Long-running sessions (PWA left open all day) still learn about updates.
      setInterval(() => void registration.update().catch(() => undefined), UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void registration.update().catch(() => undefined);
      });
    },
  });
}

export function applyUpdate(): void {
  void update?.(true);
}

/** A tapped notification asks an already open window to show its screen. */
export function onNotificationNavigate(navigate: (url: string) => void): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<{ type?: string; url?: string }>) => {
    const url = event.data?.url;
    if (event.data?.type === 'navigate' && typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) {
      navigate(url);
    }
  });
}

// ---- "Install app" offered by Chromium browsers (Android, Windows, macOS) ----

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let installEvent: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();
const notifyInstall = () => installListeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep it for the "Zainstaluj aplikację" button instead of the browser's mini-bar.
    event.preventDefault();
    installEvent = event as InstallPromptEvent;
    notifyInstall();
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    notifyInstall();
  });
}

export function subscribeInstall(listener: () => void): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}

export const canInstall = () => installEvent !== null;

export async function promptInstall(): Promise<boolean> {
  if (!installEvent) return false;
  const event = installEvent;
  await event.prompt();
  const { outcome } = await event.userChoice;
  installEvent = null;
  notifyInstall();
  return outcome === 'accepted';
}
